import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { toast } from '../../../../components/ui/toaster';

/**
 * Shown when Monaco refuses to hand the buffer back (a model past its 256M
 * character heap threshold, see monacoModelText.ts). Saving would otherwise
 * write an empty file over the user's script.
 */
export const UNREADABLE_SCRIPT_MESSAGE =
  'The script in this tab is too large to read back from the editor. Split it into smaller files.';

/**
 * The text every save / execute path must act on: Monaco's live buffer, else
 * the tab's own copy (before Monaco mounts). Returns `null` — after a toast
 * titled `refusalTitle` — when the mounted buffer cannot be read back (a model
 * past Monaco's heap-operation threshold): acting on a stale copy would save
 * or run something other than what the user sees.
 */
export function readLiveQuery(
  editor: Pick<SqlMonacoHandle, 'getValue' | 'isBufferUnreadable'> | null | undefined,
  queryRefCurrent: string | undefined,
  refusalTitle: string,
): string | null {
  if (editor?.isBufferUnreadable()) {
    toast.error({ title: refusalTitle, description: UNREADABLE_SCRIPT_MESSAGE });
    return null;
  }
  return editor?.getValue() ?? queryRefCurrent ?? '';
}

/**
 * Resolve the LIVE editor buffer for the pencil classify/open/edit, compact-VQE,
 * and inline-DDL live paths. The component's `query` state is debounced (up to
 * 200ms stale), so these paths must read Monaco's current value first, then fall
 * back to the last-known `queryRef`, then the debounced `query` (see the
 * file-wide live-path contract in QueryEditorTabContent ~line 113).
 *
 * Extracted as a pure, exported helper so the F4 regression test exercises the
 * REAL derivation instead of a copy of it. Consumed at QueryEditorTabContent
 * ~line 711 (feeds useCursorStatementEditInfo + both CompactVqePopover sites).
 */
export function resolveLiveQuery(
  editor: SqlMonacoHandle | null | undefined,
  queryRefCurrent: string | undefined,
  debouncedQuery: string,
): string {
  return editor?.getValue() ?? queryRefCurrent ?? debouncedQuery;
}
